// File: frontend/src/hooks/useFocusedEntry.ts

import { useState, useEffect } from "react";
import { EntryModel } from "../models";
import { fetchEntryLocation } from "../services/entryLocationService";
import type { CollapseEntryFetcher } from "./useCollapsedOldEntries";

export interface FocusedEntryState {
  /** True while the focused view should replace the normal paginated list. */
  active: boolean;
  /** Still resolving — the caller shows a spinner instead of an empty topic. */
  loading: boolean;
  /** The single entry to render, or null until it is resolved. */
  entry: EntryModel | null;
  /** Entries hidden ABOVE the focused one ("{n} entry daha" bar). */
  hiddenAbove: number;
  /** Entries hidden BELOW the focused one. */
  hiddenBelow: number;
  /** Page the entry sits on — where both bars send the reader. */
  page: number;
  /** Resolution failed (deleted entry, wrong topic, backend down). */
  failed: boolean;
}

const IDLE: FocusedEntryState = {
  active: false, loading: false, entry: null,
  hiddenAbove: 0, hiddenBelow: 0, page: 1, failed: false,
};

/**
 * Loads the single tanım named by "?tanim=747" together with how many entries
 * sit above and below it in the topic.
 *
 * This is the sözlük-convention permalink view: rather than dropping the reader
 * into the middle of page 8 and hoping the anchor scroll lands, the page shows
 * the one entry that was shared, framed by a "{n} entry daha" bar on each side.
 * The reader sees what was shared immediately, and the bars are the way back
 * into the full topic.
 *
 * Two requests, both reusing endpoints that already exist:
 *   1. /api/entries/{id}/location — which page the entry is on, and how many
 *      entries precede it, computed for THIS reader's page size and rookie
 *      preference (both change the answer).
 *   2. the topic's own entry page fetcher for that page, from which the entry
 *      is picked out by id.
 *
 * Step 2 fetches the whole page rather than asking for a one-entry window at
 * offset precedingCount. The list query orders by createdAt with no secondary
 * key, so a one-row window could land on a neighbour whenever two entries share
 * a timestamp; matching on id cannot.
 *
 * @param topicId    Owning topic, or null before it is known
 * @param focusEntryId Value of "?tanim", or null when absent
 * @param perPage    Reader's entries-per-page preference
 * @param fetcher    Same oldest-sort page fetcher the collapse hook uses
 */
export function useFocusedEntry(
  topicId: number | null,
  focusEntryId: number | null,
  perPage: number,
  fetcher: CollapseEntryFetcher
): FocusedEntryState {
  const [state, setState] = useState<FocusedEntryState>(IDLE);

  useEffect(() => {
    if (focusEntryId === null || topicId === null) {
      setState(IDLE);
      return;
    }

    let cancelled = false;
    setState({ ...IDLE, active: true, loading: true });

    const run = async () => {
      try {
        // showRookieEntries is deliberately true here: the shared entry must be
        // reachable even for a reader who hides rookie entries, otherwise a
        // link to a çaylak tanım would resolve to nothing for half the site.
        const location = await fetchEntryLocation(focusEntryId, perPage, true);
        if (cancelled) return;

        const res = await fetcher(location.page, perPage, false);
        if (cancelled) return;

        const entry = res.entries.find(e => e.id === focusEntryId) ?? null;
        if (!entry) {
          setState({ ...IDLE, active: true, failed: true });
          return;
        }

        const hiddenBelow = Math.max(0, location.totalItems - location.precedingCount - 1);

        setState({
          active: true,
          loading: false,
          entry,
          hiddenAbove: Math.max(0, location.precedingCount),
          hiddenBelow,
          page: location.page,
          failed: false,
        });
      } catch {
        if (!cancelled) setState({ ...IDLE, active: true, failed: true });
      }
    };

    run();
    return () => { cancelled = true; };
  }, [topicId, focusEntryId, perPage, fetcher]);

  return state;
}